import { Box, ButtonBase, FormControl, MenuItem, Select, Skeleton, Stack, Tooltip, Typography } from "@mui/material";
import { DateTime } from "luxon";
import { useEffect, useMemo, useState } from "react";
import { Link, useGetManyReference } from "react-admin";
import { assertNever } from "../../../backoffice.utils";
import { DashboardCard } from "../../../misc/DashboardCard";
import { DateRange } from "../../../model/DateRange";
import { DateWithoutTime } from "../../../model/DateWithoutTime";
import { AutovioCalendarEvent, isPracticalExam, PracticalExam, TheoryExam } from "../../../model/autovioCalendarEvents";
import { useCurrentDrivingSchoolId } from "../../../hooks/useCurrentDrivingSchoolId";
import { useEventDialog } from "../../../hooks/useEventDialog";
import { usePracticalExamDrivingLicenseClass } from "../../../hooks/usePracticalExamDrivingLicenseClass";
import { StudentDisplayName } from "../../../misc/StudentDisplayName";
import { Column } from "../../../misc/Column";
import { Row } from "../../../misc/Row";
import { calendarEventColor } from "../../../utils/calendar";

type PeriodFilter = "currentWeek" | "lastWeek";
type ExamTypeFilter = "PracticalExam" | "TheoryExam";
const WEEK_REFRESH_INTERVAL_MS = 60_000;

export function CurrentExamsCard() {
  const drivingSchoolId = useCurrentDrivingSchoolId();
  const [period, setPeriod] = useState<PeriodFilter>("currentWeek");
  const [examType, setExamType] = useState<ExamTypeFilter>("PracticalExam");
  const currentWeekStart = useCurrentBerlinWeekStart();
  const dateRange = useMemo(() => getExamDateRange(period, currentWeekStart), [currentWeekStart, period]);
  const { eventDialog, openEventDialog } = useEventDialog();
  const {
    data: exams,
    error,
    isLoading,
  } = useGetManyReference<AutovioCalendarEvent>(
    "calendarEvents",
    {
      target: "drivingSchoolId",
      id: drivingSchoolId,
      filter: { dateRange, type: examType },
      pagination: { page: 1, perPage: 9999 },
      sort: { field: "start", order: "DESC" },
    },
    { enabled: !!drivingSchoolId },
  );

  useEffect(() => {
    if (error) {
      handleError("Fehler beim Laden der aktuellen Prüfungen.", error);
    }
  }, [error]);

  return (
    <DashboardCard
      title="Prüfungen"
      sx={{
        height: "504px",
        display: "flex",
        flexDirection: "column",
        "& > .MuiBox-root:last-child": { flex: 1, minHeight: 0 },
      }}
      topRightCorner={
        <Link to={`/drivingSchools/${drivingSchoolId}/exams-calendar`} sx={{ fontSize: 14, padding: "8px" }}>
          Alle Prüfungen
        </Link>
      }
    >
      <Stack spacing={1}>
        <FormControl fullWidth size="small">
          <Select
            inputProps={{ "aria-label": "Zeitraum" }}
            value={period}
            onChange={(event) => setPeriod(event.target.value as PeriodFilter)}
          >
            <MenuItem value="currentWeek">Aktuelle Woche</MenuItem>
            <MenuItem value="lastWeek">Letzte Woche</MenuItem>
          </Select>
        </FormControl>
        <FormControl fullWidth size="small">
          <Select
            inputProps={{ "aria-label": "Prüfungsart" }}
            value={examType}
            onChange={(event) => setExamType(event.target.value as ExamTypeFilter)}
          >
            <MenuItem value="PracticalExam">Praktische Prüfungen</MenuItem>
            <MenuItem value="TheoryExam">Theorie Prüfungen</MenuItem>
          </Select>
        </FormControl>
      </Stack>
      {isLoading ? (
        <CurrentExamsSkeleton />
      ) : error ? (
        <CardMessage>Die aktuellen Prüfungen konnten nicht geladen werden.</CardMessage>
      ) : !exams || exams.length === 0 ? (
        <CardMessage>Keine Prüfungen für die gewählten Filter.</CardMessage>
      ) : (
        <Column sx={{ gap: 0, overflowY: "auto", minHeight: 0 }}>
          {exams.map((exam) => (
            <CurrentExamListItem key={exam.id} exam={exam} onClick={() => openEventDialog(exam)} />
          ))}
        </Column>
      )}
      {eventDialog}
    </DashboardCard>
  );
}

export function getExamDateRange(period: PeriodFilter, now = DateTime.now().setZone("Europe/Berlin")): DateRange {
  const selectedWeek = period === "lastWeek" ? now.minus({ weeks: 1 }) : now;
  return [
    DateWithoutTime.fromDateTime(selectedWeek.startOf("week")),
    DateWithoutTime.fromDateTime(selectedWeek.endOf("week")),
  ];
}

function useCurrentBerlinWeekStart(): DateTime {
  const [weekStartMillis, setWeekStartMillis] = useState(getCurrentBerlinWeekStartMillis);

  useEffect(() => {
    const refreshWeekStart = () => {
      const nextWeekStartMillis = getCurrentBerlinWeekStartMillis();
      setWeekStartMillis((currentWeekStartMillis) =>
        currentWeekStartMillis === nextWeekStartMillis ? currentWeekStartMillis : nextWeekStartMillis,
      );
    };
    const intervalId = window.setInterval(refreshWeekStart, WEEK_REFRESH_INTERVAL_MS);
    window.addEventListener("focus", refreshWeekStart);
    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener("focus", refreshWeekStart);
    };
  }, []);

  return useMemo(() => DateTime.fromMillis(weekStartMillis, { zone: "Europe/Berlin" }), [weekStartMillis]);
}

function getCurrentBerlinWeekStartMillis(): number {
  return DateTime.now().setZone("Europe/Berlin").startOf("week").toMillis();
}

function CurrentExamListItem(props: { exam: AutovioCalendarEvent; onClick: () => void }) {
  const { exam, onClick } = props;
  const practicalExam = isPracticalExam(exam) ? exam : undefined;
  const practicalExamDrivingLicenseClass = usePracticalExamDrivingLicenseClass(practicalExam);

  return (
    <ButtonBase
      onClick={onClick}
      sx={{
        display: "grid",
        gridTemplateColumns: "32px minmax(0, 1fr) auto",
        alignItems: "center",
        gap: 1.25,
        width: "100%",
        minWidth: 0,
        py: 1,
        textAlign: "left",
      }}
    >
      <Box
        component="time"
        dateTime={exam.start.toISODate() ?? undefined}
        sx={{
          display: "flex",
          flexDirection: "column",
          width: 32,
          height: 36,
          overflow: "hidden",
          borderRadius: 0.5,
          backgroundColor: "action.hover",
        }}
      >
        <Typography
          sx={{
            display: "flex",
            height: 14,
            flexShrink: 0,
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: calendarEventColor(exam),
            color: (theme) => (theme.palette.mode === "dark" ? theme.palette.common.black : theme.palette.common.white),
            fontSize: 9,
            fontWeight: 700,
            lineHeight: 1,
            textTransform: "uppercase",
          }}
        >
          {exam.start.setLocale("de").toFormat("LLL")}
        </Typography>
        <Typography
          sx={{
            display: "flex",
            flex: 1,
            alignItems: "center",
            justifyContent: "center",
            fontSize: 14,
            fontWeight: 700,
            lineHeight: 1,
          }}
        >
          {exam.start.toFormat("d")}
        </Typography>
      </Box>
      <Box sx={{ minWidth: 0, ml: 0.5 }}>
        {"student" in exam && (
          <StudentDisplayName
            studentId={exam.student.uid}
            fontSize={14}
            noWrap
            sx={{ display: "block", width: "100%" }}
          />
        )}
        <Typography noWrap sx={{ color: "text.secondary", fontSize: 12 }}>
          {exam.start.toFormat("HH:mm")} Uhr ·{" "}
          {practicalExam ? `Klasse ${practicalExamDrivingLicenseClass ?? "–"}` : "Theorieprüfung"}
        </Typography>
      </Box>
      <ExamStatusIndicator status={exam.status} />
    </ButtonBase>
  );
}

function ExamStatusIndicator(props: { status: PracticalExam["status"] | TheoryExam["status"] }) {
  const { status } = props;
  const indicator = getExamStatusIndicator(status);

  if (!indicator) {
    return null;
  }

  return (
    <Tooltip title={indicator.label} arrow>
      <Box
        component="span"
        role="img"
        aria-label={`Status: ${indicator.label}`}
        sx={{ flexShrink: 0, fontSize: 18, lineHeight: 1 }}
      >
        {indicator.emoji}
      </Box>
    </Tooltip>
  );
}

function getExamStatusIndicator(status: PracticalExam["status"] | TheoryExam["status"]) {
  switch (status) {
    case "passed":
      return { label: "Bestanden", emoji: "👍" } as const;
    case "failed":
      return { label: "Nicht bestanden", emoji: "👎" } as const;
    case "canceledByStudent":
    case "canceledByInstructor":
      return { label: "Abgesagt", emoji: "❌" } as const;
    case "declinedByStudent":
      return { label: "Abgelehnt", emoji: "❌" } as const;
    case "no-show":
      return { label: "Nicht erschienen", emoji: "❌" } as const;
    case "invited":
    case "booked":
    case "finished":
    case undefined:
      return undefined;
    default:
      return assertNever(status);
  }
}

function CardMessage(props: React.PropsWithChildren) {
  const { children } = props;
  return (
    <Box sx={{ display: "flex", flex: 1, alignItems: "center", justifyContent: "center" }}>
      <Typography sx={{ color: "text.secondary", fontSize: 14, textAlign: "center" }}>{children}</Typography>
    </Box>
  );
}

function CurrentExamsSkeleton() {
  return (
    <Column role="status" aria-label="Prüfungen werden geladen" sx={{ gap: 0 }}>
      {Array.from({ length: 6 }, (_value, index) => (
        <Row key={index} alignItems="center" sx={{ gap: 1.25, py: 1 }}>
          <Box sx={{ width: 32, height: 36, flexShrink: 0, overflow: "hidden", borderRadius: 1 }}>
            <Skeleton variant="rectangular" width="100%" height={14} />
            <Skeleton variant="rectangular" width="100%" height={22} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0, ml: 0.5 }}>
            <Skeleton variant="text" width="55%" sx={{ fontSize: 14 }} />
            <Skeleton variant="text" width="40%" sx={{ fontSize: 12 }} />
          </Box>
          <Skeleton variant="circular" width={18} height={18} />
        </Row>
      ))}
    </Column>
  );
}
